import { observer } from 'mobx-react-lite';

import { answerOf, type MobxQuery } from 'mobx/MobxQuery';
import type { OpenPeriodsPreview as Preview } from 'api/scheduling_windows/operations/previewOpenPeriods';

import OpeningMomentsModal from './OpeningMomentsModal';

interface Props {
  readonly query: MobxQuery<number, Preview>;
  readonly onHide: () => void;
}

/**
 * The open-periods preview: a thin observer that projects a `MobxQuery` onto the
 * prop-driven `OpeningMomentsModal` (which the submit page also uses, with periods
 * it already holds).
 *
 * Reading `query.value` here is what arms the query — so the evaluation starts when
 * this mounts and is torn down when it unmounts, with no explicit fetch call and
 * nothing to clear on close. That replaces the `evaluateSchedulingWindow` thunk and
 * the `clearEvaluation` action it needed.
 *
 * While a re-evaluation is in flight the previous periods stay on screen (`stale`),
 * so the table does not blank out and re-appear.
 */
export const OpenPeriodsPreview = observer<Props>(({ query, onHide }) => {
  const value = query.value;
  const held = answerOf(value);

  return (
    <OpeningMomentsModal
      show
      onHide={onHide}
      periods={held?.value.periods ?? []}
      isLoading={value.kind === 'loading' || value.kind === 'stale'}
      error={value.kind === 'error' ? value.error.message : null}
      windowName={held?.value.windowName ?? ''}
      timezone={held?.value.timezone}
      asOf={held?.asOf}
      onRefresh={() => void query.refresh()}
    />
  );
});
